/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect } from 'react';
import { useDataModel } from '@thd-nucleus/data-sources';
import { string, func } from 'prop-types';
import { useStoreId } from '@thd-nucleus/experience-context';
import { withDynamicComponent } from '@thd-nucleus/app-render';
import { withLayoutGroup } from '@thd-olt-component-react/layout-manager';
import { withHydrator } from '@thd-olt-component-react/hydrator';
import { LoadingPlaceholder } from '@thd-olt-component-react/loading-placeholder';
import { PackageHeader } from './subcomponents/PackageHeader';
import { PackageInfo } from './subcomponents/PackageInfo';
import { PackagesMiniContext } from './PackagesMiniContext';
import { MAJOR_APPLIANCE_TYPE } from '../constants';

import './packages-mini.scss';
import { dataModel } from './dataModel';

const determineIfMajorAppliancePackage = (apiResponsePackage) => {
  return apiResponsePackage?.products.every(
    (item) => item.identifiers.productType === MAJOR_APPLIANCE_TYPE
  );
};

const attachAlternateProductsAndExtendProductInfo = ({ newPackageModel, productGrouping, products }) => {
  // eslint-disable-next-line no-unused-expressions
  products?.forEach((item) => {
    let itemProductGroup = item.info.productGroup;
    const productGroup = productGrouping[itemProductGroup];
    const isSelected = productGroup?.default === item.identifiers.itemId;
    if (isSelected) newPackageModel.selectedProductIds.push(item.identifiers.itemId);
    newPackageModel.products.set(item.identifiers.itemId, item);
  });
  return newPackageModel;
};

export const organizeProductsByClassAndSubclass = (apiResponsePackage) => {
  const productGrouping = {};
  const newPackageModel = {
    metadata: { ...apiResponsePackage.metadata },
    products: new Map(),
    selectedProductIds: [],
    totalPricing: {},
    isMajorAppliancePackage: determineIfMajorAppliancePackage(apiResponsePackage)
  };
  /* eslint-disable-next-line no-unused-expressions */
  apiResponsePackage.products.forEach((item) => {
    let itemProductGroup = item.info.productGroup;
    const alternateProduct = {
      itemId: item.identifiers.itemId,
      price: item.pricing.value,
      hasMap: item.pricing.mapAboveOriginalPrice !== null,
      label: item.info.label
    };

    if (productGrouping[itemProductGroup]) {
      const productGroup = productGrouping[itemProductGroup];
      productGroup.products.push(alternateProduct);
    } else {
      productGrouping[itemProductGroup] = {
        products: [alternateProduct],
        default: item.identifiers.itemId
      };
    }
  });
  return attachAlternateProductsAndExtendProductInfo({
    newPackageModel,
    productGrouping,
    products: apiResponsePackage.products
  });
};

const getSelectedProducts = (newPackageModel) => {
  const {
    selectedProductIds: selectedIds
  } = newPackageModel;
  return selectedIds.reduce((acc, curr) => acc.concat(newPackageModel.products.get(curr)), []) || [];
};

const PackagesMiniComponent = ({ itemId, callOnRender }) => {
  useEffect(() => { LIFE_CYCLE_EVENT_BUS.lifeCycle.trigger('packages-mini.ready'); }, []);

  const storeId = useStoreId();

  const { data } = useDataModel('packages', {
    variables: {
      itemId,
      storeId,
      limit: 1
    },
    ssr: true
  });

  useEffect(() => {
    if (data?.packages?.[0]?.metadata
      && data?.packages?.[0]?.products) {
      callOnRender();
    }
  }, [data]);

  if (!data?.packages?.[0]?.metadata || !data?.packages?.[0]?.products) {
    return null;
  }

  const newModel = organizeProductsByClassAndSubclass(data.packages[0]);
  const selectedProducts = getSelectedProducts(newModel);
  const packageCategory = data?.packages[0]?.metadata?.packageCategory;

  return (
    <div className="packages-mini" data-testid="packages-mini">
      <PackagesMiniContext.Provider value={{
        products: selectedProducts,
        newModel,
        packageCategory
      }}
      >
        <PackageHeader />
        <PackageInfo />
      </PackagesMiniContext.Provider>
    </div>
  );
};

const propTypes = {
  itemId: string,
  callOnRender: func
};

const defaultProps = {
  itemId: '',
  callOnRender: () => {},
};

PackagesMiniComponent.displayName = 'PackagesMini';
PackagesMiniComponent.propTypes = propTypes;
PackagesMiniComponent.defaultProps = defaultProps;
PackagesMiniComponent.dataModel = dataModel;

const HydratedComponent = withHydrator({
  id: 'product-section-pacakges-mini',
  scrollBuffer: 100,
  delay: 1500,
  preserveCtxVal: 'clientStore',
  placeholder: (
    <LoadingPlaceholder
      color="#E0E0E0"
      ready={false}
      style={{ height: 750 }}
      type="rect"
    >
      <div />
    </LoadingPlaceholder>
  )
}, PackagesMiniComponent);

const DynamicComponent = withDynamicComponent(HydratedComponent);
const PackagesMini = withLayoutGroup({
  name: 'packages-mini'
}, DynamicComponent);
PackagesMini.propTypes = propTypes;
PackagesMini.defaultProps = defaultProps;
PackagesMini.dataModel = dataModel;
PackagesMini.displayName = 'PackagesMini';
export { PackagesMini };
